import React from 'react';
import { Link } from 'react-router-dom';
import Seo from './Seo';
import Breadcrumbs from './Breadcrumbs';
import { breadcrumbSchema } from '../lib/schema';
import { whatsappLink, track } from '../lib/contact';
import { runtime } from '../data/runtime';

const PATHS = {
  check: <path d="M20 6 9 17l-5-5" />,
  x: <path d="M18 6 6 18M6 6l12 12" />,
  pin: <><path d="M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></>,
  phone: <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  car: <><path d="M5 17H3v-5l2-5h14l2 5v5h-2" /><circle cx="7.5" cy="17" r="2" /><circle cx="16.5" cy="17" r="2" /><path d="M9.5 17h5M3 12h18" /></>,
  sparkle: <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />,
  drop: <path d="M12 2.7s6 6.5 6 11.3a6 6 0 0 1-12 0c0-4.8 6-11.3 6-11.3z" />,
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  clock: <><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></>,
  building: <><rect x="4" y="2" width="16" height="20" rx="1" /><path d="M9 22v-4h6v4M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01" /></>,
  home: <><path d="M3 10l9-7 9 7v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><path d="M9 22V12h6v10" /></>,
  truck: <><path d="M1 3h15v13H1zM16 8h4l3 3v5h-7z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></>,
  tag: <><path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L2 12V2h10l8.6 8.6a2 2 0 0 1 0 2.8z" /><circle cx="7" cy="7" r="1.5" /></>,
  chat: <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.2A8.4 8.4 0 0 1 12 3a8.5 8.5 0 0 1 9 8.5z" />,
  arrow: <path d="M5 12h14M13 5l7 7-7 7" />,
  users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" /></>,
  star: <path d="m12 2 3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1z" />,
  play: <path d="M7 4.5v15l13-7.5z" />
};

export const Icon = ({ name, size = 24, className = '' }) => (
  <svg className={`icon ${className}`} width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    {PATHS[name]}
  </svg>
);

export const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.4-.5c.2-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3zM12 21.8a9.9 9.9 0 0 1-5-1.4l-.4-.2-3.7 1 1-3.6-.2-.4A9.9 9.9 0 1 1 12 21.8zm8.4-18.3A11.8 11.8 0 0 0 .2 11.9c0 2.1.5 4.1 1.6 5.9L.1 24l6.3-1.7a11.9 11.9 0 0 0 5.7 1.4c6.5 0 11.9-5.3 11.9-11.9 0-3.2-1.2-6.2-3.5-8.4z" />
  </svg>
);

export const PageMeta = ({ title, description, path, crumbs, noindex, jsonLd = [] }) => (
  <>
    <Seo title={title} description={description} path={path} noindex={noindex}
      jsonLd={[...(crumbs ? [breadcrumbSchema([{ name: 'Home', path: '/' }, ...crumbs])] : []), ...jsonLd]} />
    {crumbs && <Breadcrumbs items={crumbs} />}
  </>
);

export const PageHero = ({ eyebrow, title, children, actions, compact }) => (
  <section className={`page-hero${compact ? ' page-hero--compact' : ''}`}>
    <div className="container">
      {eyebrow && <p className="eyebrow eyebrow--light">{eyebrow}</p>}
      <h1>{title}</h1>
      {children && <div className="page-hero__lead">{children}</div>}
      {actions && <div className="btn-row">{actions}</div>}
    </div>
  </section>
);

export const SectionHead = ({ eyebrow, title, children, align = 'center', as: H = 'h2' }) => (
  <div className={`section-head section-head--${align}`}>
    {eyebrow && <p className="eyebrow">{eyebrow}</p>}
    <H>{title}</H>
    {children && <p className="section-head__lead">{children}</p>}
  </div>
);

export const Price = ({ value, suffix }) => (
  <span className="price"><span className="price__cur">AED</span> {value}{suffix && <span className="price__suffix"> {suffix}</span>}</span>
);

export const CheckList = ({ items, variant = 'check' }) => (
  <ul className={`checklist checklist--${variant}`}>
    {items.map((item) => (
      <li key={item}><Icon name={variant} size={18} />{item}</li>
    ))}
  </ul>
);

export const PackageCard = ({ pkg, headingLevel = 3, showExcludes = false }) => {
  const H = `h${headingLevel}`;
  return (
    <article className={`pkg-card${pkg.featured ? ' pkg-card--featured' : ''}`}>
      {pkg.featured && <span className="pkg-card__badge">Most booked</span>}
      <H className="pkg-card__name">{pkg.name}</H>
      <p className="pkg-card__summary">{pkg.summary}</p>
      <div className="pkg-card__price"><Price value={pkg.price} suffix="/ wash" /></div>
      <p className="pkg-card__meta"><Icon name="clock" size={16} /> {pkg.duration}</p>
      <CheckList items={pkg.includes} />
      {showExcludes && pkg.excludes.length > 0 && (
        <>
          <p className="pkg-card__subhead">Not included</p>
          <CheckList items={pkg.excludes} variant="x" />
        </>
      )}
      <div className="pkg-card__actions">
        <AppButton label={`Book ${pkg.name}`}
          className={`btn ${pkg.featured ? 'btn-primary' : 'btn-outline-dark'} btn-block`}
          source="package_card" params={{ package: pkg.id }} />
        <Link to={pkg.servicePage} className="link-arrow">What's included <Icon name="arrow" size={16} /></Link>
      </div>
    </article>
  );
};

// Primary conversion CTA: the Android app is the preferred way to order.
export const AppButton = ({ label = 'Get the app', className = 'btn btn-primary', source = 'site', params = {} }) => (
  <a href={runtime.BUSINESS.playStore} target="_blank" rel="noopener noreferrer" className={className}
    onClick={() => track('app_store_click', { store: 'google_play', source, ...params })}>
    <Icon name="play" size={18} /> {label}
  </a>
);

export const StoreBadge = ({ source = 'site' }) => (
  <a href={runtime.BUSINESS.playStore} target="_blank" rel="noopener noreferrer" className="store-badge"
    onClick={() => track('app_store_click', { store: 'google_play', source })}>
    <img src="/img/google-play.png" alt="Get the Quick Way app on Google Play" width="180" height="54" loading="lazy" />
  </a>
);

export const WhatsAppButton = ({ text, label = 'Chat on WhatsApp', className = 'btn btn-whatsapp', source }) => (
  <a href={whatsappLink(text)} className={className} target="_blank" rel="noopener noreferrer"
    onClick={() => track('whatsapp_click', { source })}>
    <WhatsAppIcon /> {label}
  </a>
);

export const CtaBand = ({ title = 'Order your next wash in the app', text = 'Download the Quick Way app for Android, drop a pin where your car is parked and pick a package. WhatsApp works too.', source = 'cta_band' }) => (
  <section className="cta-band">
    <div className="container cta-band__inner">
      <div>
        <h2>{title}</h2>
        <p>{text}</p>
      </div>
      <div className="btn-row">
        <AppButton label="Get it on Google Play" className="btn btn-primary btn-lg" source={source} />
        <a href={`tel:${runtime.BUSINESS.phone}`} className="btn btn-ghost btn-lg" onClick={() => track('call_click', { source })}>
          <Icon name="phone" size={18} /> {runtime.BUSINESS.phoneDisplay}
        </a>
      </div>
    </div>
  </section>
);

export const FaqList = ({ items }) => (
  <div className="faq-list">
    {items.map((f) => (
      <details key={f.q} className="faq-item">
        <summary><h3>{f.q}</h3><span className="faq-item__icon" aria-hidden="true" /></summary>
        <p>{f.a}</p>
      </details>
    ))}
  </div>
);

export const Field = ({ label, id, error, hint, required, children }) => (
  <div className={`field${error ? ' field--error' : ''}`}>
    <label htmlFor={id}>{label}{required && <span className="req" aria-hidden="true"> *</span>}</label>
    {children}
    {hint && !error && <p className="field__hint" id={`${id}-hint`}>{hint}</p>}
    {error && <p className="field__error" id={`${id}-error`} role="alert">{error}</p>}
  </div>
);

export const describedBy = (id, error, hint) => (error ? `${id}-error` : hint ? `${id}-hint` : undefined);
